import { Box, Divider, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { AwardLetterTypeDto } from "../../../../autogen/bff-api";
import { rejectionIntro, SIGN_OFF, statutoryClauses, StatutoryClauseContext, winnerIntro } from "./awardLetterTemplates";

export type AwardLetterPreviewProps = {
  type: AwardLetterTypeDto;
  supplierName: string;
  winnerName: string;
  eventTitle: string;
  senderName?: string | null;
  criterionJustifications: Record<string, string>;
  freeText?: string | null;
  criteria: { id: string; name: string }[];
  scores?: Record<string, { supplierPoints: number | null; winnerPoints: number | null }>;
  /** When the letter was dispatched; the authoritative letter date. Falls back to today for buyer drafts. */
  sentAt?: string | null;
  clauseContext: StatutoryClauseContext;
  scoreHeading: string;
};

export const AwardLetterPreview = ({
  type,
  supplierName,
  winnerName,
  eventTitle,
  senderName,
  criterionJustifications,
  freeText,
  criteria,
  scores,
  sentAt,
  clauseContext,
  scoreHeading,
}: AwardLetterPreviewProps) => {
  const { t } = useTranslation();
  const isRejection = type === AwardLetterTypeDto.Rejection;
  const intro = isRejection ? rejectionIntro(eventTitle, winnerName) : winnerIntro(eventTitle);
  const clauses = statutoryClauses(clauseContext);
  const justifiedCriteria = criteria.filter((c) => criterionJustifications[c.id]?.trim());
  const letterDate = (sentAt ? new Date(sentAt) : new Date()).toLocaleDateString("nb-NO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p={{ base: "6", md: "10" }}>
      <Stack spacing="1" mb="6">
        <Text fontWeight="semibold" color="smText">
          {senderName || t("The contracting authority")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {letterDate}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("Reference")}: {eventTitle}
        </Text>
        <Text fontSize="sm" color="smMuted" mt="2">
          {t("To")}: {supplierName}
        </Text>
      </Stack>

      <Text fontWeight="semibold" fontSize="lg" color="smText" mb="4">
        {t("Notification of contract award")}
      </Text>

      <Text color="smText" whiteSpace="pre-wrap" mb="5">
        {intro}
      </Text>

      {isRejection && scores && justifiedCriteria.length > 0 && (
        <Box mb="5" overflowX="auto">
          <Text fontWeight="medium" color="smText" mb="2">
            {scoreHeading}
          </Text>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Criterion")}</Th>
                <Th isNumeric>{t("Your score")}</Th>
                <Th isNumeric>{t("Winner's score")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {justifiedCriteria.map((c) => {
                const s = scores[c.id];
                return (
                  <Tr key={c.id}>
                    <Td>{c.name}</Td>
                    <Td isNumeric fontFamily="mono">
                      {s?.supplierPoints ?? "–"}
                    </Td>
                    <Td isNumeric fontFamily="mono">
                      {s?.winnerPoints ?? "–"}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {isRejection && justifiedCriteria.length > 0 && (
        <Stack spacing="3" mb="5">
          <Text fontWeight="medium" color="smText">
            {t("Justification per criterion")}
          </Text>
          {justifiedCriteria.map((c) => (
            <Box key={c.id}>
              <Text fontWeight="medium" fontSize="sm" color="smText">
                {c.name}
              </Text>
              <Text color="smText" whiteSpace="pre-wrap">
                {criterionJustifications[c.id]}
              </Text>
            </Box>
          ))}
        </Stack>
      )}

      {freeText?.trim() && (
        <Text color="smText" whiteSpace="pre-wrap" mb="5">
          {freeText}
        </Text>
      )}

      {(clauses.standstill || clauses.complaint) && (
        <>
          <Divider my="5" />

          <Stack spacing="3" fontSize="sm" color="smMuted">
            {clauses.standstill && (
              <Box>
                <Text fontWeight="medium" color="smText">
                  {t("Standstill period")}
                </Text>
                <Text whiteSpace="pre-wrap">{clauses.standstill}</Text>
              </Box>
            )}
            {clauses.complaint && (
              <Box>
                <Text fontWeight="medium" color="smText">
                  {t("Right to complain")}
                </Text>
                <Text whiteSpace="pre-wrap">{clauses.complaint}</Text>
              </Box>
            )}
          </Stack>
        </>
      )}

      <Text color="smMuted" whiteSpace="pre-wrap" mt="6">
        {SIGN_OFF}
      </Text>
    </Box>
  );
};
